/**
 * SurebetService - API service for surebet data management
 * 
 * Features:
 * - Get markets analysis with bookmaker filtering
 * - Response validation and transformation
 * - Error handling for API failures
 * - Retry logic for network failures
 * - HTTP 402 handling for subscription requirements
 * - Caching support
 * - Rate limiting protection
 */

import apiClient from '../../api/config/ApiClient';
import { VALIDATION_RULES } from '../constants/surebetTypes';
import { convertBookmakerIdsToApiFormat } from '../../../utils/bookmakerUtils';
import { normalizeNearArbPayload } from '../utils/nearArbPayload';

class SurebetService {
  /**
   * Cache configuration
   */
  static cache = {
    data: null,
    timestamp: null,
    ttl: 60000, // 1 minute cache TTL
  };

  /**
   * Masked data cache (separate from main cache)
   */
  static maskedCache = {
    data: null,
    timestamp: null,
    ttl: 300000, // 5 minutes cache TTL for masked data
  };

  /**
   * Empty public preview payload (GET /api/core/markets-analysis/masked)
   */
  static EMPTY_MASKED_PREVIEW = {
    maskedItems: [],
    demoItem: null,
    totalActiveOpportunities: 0,
    totalActiveEvents: 0,
  };

  /**
   * Request deduplication - prevent multiple simultaneous calls to same endpoint
   */
  static pendingRequests = new Map();

  /**
   * Singleton instance for masked data - ensures only one active request at a time
   */
  static maskedDataInstance = null;

  /**
   * Retry configuration
   */
  static retryConfig = {
    maxRetries: 3,
    retryDelay: 2000, // 2 seconds
    retryableStatusCodes: [408, 429, 500, 502, 503, 504],
  };

  /**
   * Get masked markets analysis data (public endpoint)
   * 
   * This endpoint is publicly accessible and returns markets analysis data
   * with sensitive information masked (team names, bookmaker names, event details).
   * 
   * @returns {Promise<{data: {maskedItems: Array, demoItem: Object|null, totalActiveOpportunities: number, totalActiveEvents: number}, error: string|null}>}
   */
  static async getMaskedMarketsAnalysis() {
    const endpoint = '/api/core/markets-analysis/masked';
    const requestId = `masked-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    
    try {
      console.log(`🔍 SurebetService (Masked) [${requestId}]: Starting request to`, endpoint);
      console.log(`🔍 SurebetService (Masked) [${requestId}]: Pending requests:`, Array.from(this.pendingRequests.keys()));
      
      // Check cache first - if we have valid cached data, return it immediately
      if (this.isMaskedCacheValid()) {
        console.log(`📦 SurebetService (Masked) [${requestId}]: Using cached data`);
        return { data: this.maskedCache.data, error: null };
      }

      // Check if request is already in progress (deduplication)
      if (this.pendingRequests.has(endpoint)) {
        console.log(`⏳ SurebetService (Masked) [${requestId}]: Request already in progress, waiting...`);
        const result = await this.pendingRequests.get(endpoint);
        console.log(`✅ SurebetService (Masked) [${requestId}]: Got result from pending request:`, result);
        return result;
      }

      console.log(`🚀 SurebetService (Masked) [${requestId}]: Making new API request`);

      // Create new request promise that includes full processing
      const requestPromise = this.processMaskedRequest(requestId);

      // Store the promise to prevent duplicate requests
      this.pendingRequests.set(endpoint, requestPromise);
      console.log(`🔒 SurebetService (Masked) [${requestId}]: Stored request in pending map`);

      const result = await requestPromise;

      // Clear the pending request
      this.pendingRequests.delete(endpoint);
      console.log(`🔓 SurebetService (Masked) [${requestId}]: Removed request from pending map`);

      return result;
    } catch (error) {
      // Clear the pending request on error
      this.pendingRequests.delete(endpoint);
      console.log(`🔓 SurebetService (Masked) [${requestId}]: Removed request from pending map due to error`);
      
      console.error(`❌ SurebetService (Masked) [${requestId}] error:`, error);
      console.error(`❌ Error details [${requestId}]:`, {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data
      });
      
      return { data: { ...this.EMPTY_MASKED_PREVIEW }, error: this.handleError(error) };
    }
  }

  /**
   * Process masked request with full transformation and caching
   */
  static async processMaskedRequest(requestId) {
    const endpoint = '/api/core/markets-analysis/masked';
    
    try {
      console.log(`🌐 SurebetService (Masked) [${requestId}]: Executing API call`);
      
      const responseData = await this.retryApiCall(async () => {
        return await apiClient.get(endpoint, { 
          minimalHeaders: true  // Public endpoint, no auth headers needed
        });
      });

      console.log(`🔍 SurebetService (Masked) [${requestId}]: Raw response data:`, responseData);
      
      if (!responseData) {
        throw new Error('No response received from server');
      }
      
      if (responseData === null || responseData === undefined) {
        console.warn(`⚠️ SurebetService (Masked) [${requestId}]: Server returned null/undefined data for masked endpoint`);
        return { data: { ...this.EMPTY_MASKED_PREVIEW }, error: null };
      }

      const preview = this.transformMaskedPreviewResponse(responseData);
      console.log(`✅ SurebetService (Masked) [${requestId}]: Preview — masked: ${preview.maskedItems.length}, demo: ${preview.demoItem ? 'yes' : 'no'}, totals: ${preview.totalActiveOpportunities}`);

      this.updateMaskedCache(preview);
      console.log(`💾 SurebetService (Masked) [${requestId}]: Updated cache`);

      return { data: preview, error: null };
    } catch (error) {
      console.error(`❌ SurebetService (Masked) [${requestId}] processing error:`, error);
      console.error(`❌ SurebetService (Masked) [${requestId}] error details:`, {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
        hasResponse: !!error.response
      });
      
      // Check for 401 with invalid token and dispatch event here as fallback
      if (error.response?.status === 401) {
        const errorData = error.response?.data || {};
        const detail = errorData.detail || errorData.message || '';
        const description = errorData.description || '';
        const combinedText = `${detail} ${description}`.toLowerCase();
        
        if (combinedText.includes('invalid or inactive token') ||
            combinedText.includes('inactive') ||
            combinedText.includes('revoked')) {
          console.log('🚨 SurebetService: Invalid token detected, dispatching sessionConflict event (fallback)');
          window.dispatchEvent(new CustomEvent('auth:sessionConflict', {
            detail: {
              reason: 'session_invalid',
              message: 'Your session is not valid or has been revoked. Please log in again.',
              endpoint: error.config?.url,
              status: 401,
              timestamp: new Date().toISOString(),
              errorData: errorData
            }
          }));
        }
      }
      
      return { data: { ...this.EMPTY_MASKED_PREVIEW }, error: this.handleError(error) };
    }
  }

  /**
   * Get markets analysis data
   * 
   * @param {Object} params - Query parameters
   * @param {Array<string>} params.bookmakers - Array of bookmaker identifiers
   * @param {string} params.sport - Sport filter
   * @param {string} params.championship - Championship filter
   * @param {boolean} params.profitableOnly - Return only profitable surebets
   * @param {boolean} params.useCache - Use cached data if available
   * @returns {Promise<{data: Array, error: string|null}>}
   */
  static async getMarketsAnalysis(params = {}) {
    const requestId = `markets-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    
    try {
      const {
        bookmakers = [],
        sport = null,
        championship = null,
        profitableOnly = true,
        useCache = true,
      } = params;

      console.log(`🔍 SurebetService (Markets) [${requestId}]: Starting request with params:`, params);

      // Check cache if enabled
      if (useCache && this.isCacheValid()) {
        console.log(`📦 SurebetService (Markets) [${requestId}]: Using cached data`);
        return { data: this.cache.data, error: null };
      }

      // Build query parameters
      const queryParams = this.buildQueryParams({
        bookmakers,
        sport,
        championship,
        profitableOnly,
      });

      // Create endpoint key for deduplication
      const endpoint = '/api/core/markets-analysis';
      const endpointKey = `${endpoint}?${JSON.stringify(queryParams)}`;

      console.log(`🔍 SurebetService (Markets) [${requestId}]: Endpoint key:`, endpointKey);
      console.log(`🔍 SurebetService (Markets) [${requestId}]: Pending requests:`, Array.from(this.pendingRequests.keys()));

      // Check if request is already in progress (deduplication)
      if (this.pendingRequests.has(endpointKey)) {
        console.log(`⏳ SurebetService (Markets) [${requestId}]: Request already in progress, waiting...`);
        const result = await this.pendingRequests.get(endpointKey);
        console.log(`✅ SurebetService (Markets) [${requestId}]: Got result from pending request`);
        return result;
      }

      console.log(`🚀 SurebetService (Markets) [${requestId}]: Making new API request`);

      // Create new request promise that includes full processing
      const requestPromise = this.processMarketsRequest(requestId, queryParams);

      // Store the promise to prevent duplicate requests
      this.pendingRequests.set(endpointKey, requestPromise);
      console.log(`🔒 SurebetService (Markets) [${requestId}]: Stored request in pending map`);

      const result = await requestPromise;

      // Clear the pending request
      this.pendingRequests.delete(endpointKey);
      console.log(`🔓 SurebetService (Markets) [${requestId}]: Removed request from pending map`);

      return result;
    } catch (error) {
      // Clear the pending request on error
      const endpoint = '/api/core/markets-analysis';
      const queryParams = this.buildQueryParams(params);
      const endpointKey = `${endpoint}?${JSON.stringify(queryParams)}`;
      this.pendingRequests.delete(endpointKey);
      console.log(`🔓 SurebetService (Markets) [${requestId}]: Removed request from pending map due to error`);
      
      return this.handleError(error);
    }
  }

  /**
   * Near-arb feed: events above the server threshold for the configured bookmakers.
   * Does not send the local bookmaker filter. 402 is a distinct Ultimate error so
   * the generic activate-subscription modal stays closed.
   *
   * @returns {Promise<{data: Array, meta: Object|null, error: string|null}>}
   */
  static async getNearArbAnalysis() {
    try {
      const responseData = await apiClient.get('/api/core/markets-analysis/near-arb', {
        minimalHeaders: true,
      });

      const payload = normalizeNearArbPayload(responseData);
      const transformedData = this.validateData(this.transformResponse(payload.items));

      return {
        data: transformedData,
        meta: {
          bookmakers: payload.bookmakers,
          minMargin: payload.minMargin,
        },
        error: null,
      };
    } catch (error) {
      const status = error.status || error.response?.status;
      if (status === 402 || error.code === 'ULTIMATE_REQUIRED') {
        return { data: [], meta: null, error: 'ULTIMATE_REQUIRED' };
      }
      if (status === 404) {
        return { data: [], meta: null, error: 'NEAR_ARB_UNAVAILABLE' };
      }
      return {
        data: null,
        meta: null,
        error: error.message || 'Failed to load near-arb data',
      };
    }
  }

  /**
   * Process markets request with full transformation and caching
   */
  static async processMarketsRequest(requestId, queryParams) {
    try {
      console.log(`🌐 SurebetService (Markets) [${requestId}]: Executing API call`);
      
      // Make API request with retry logic
      const responseData = await this.retryApiCall(async () => {
        return await apiClient.get('/api/core/markets-analysis', { 
          params: queryParams,
          minimalHeaders: true  // Bypass custom headers to avoid CORS preflight issues
        });
      });

      // Validate response - the axios interceptor returns data directly, not { data: ... }
      console.log(`🔍 SurebetService (Markets) [${requestId}]: Raw response data:`, responseData);
      
      if (!responseData) {
        throw new Error('No response received from server');
      }
      
      // If data is null or undefined, return empty array
      if (responseData === null || responseData === undefined) {
        console.warn(`⚠️ SurebetService (Markets) [${requestId}]: Server returned null/undefined data, returning empty array`);
        return { data: [], error: null };
      }

      // Transform and validate data
      console.log(`🔍 SurebetService (Markets) [${requestId}]: Starting transformation...`);
      const transformedData = this.transformResponse(responseData);
      console.log(`✅ SurebetService (Markets) [${requestId}]: Transformed data:`, transformedData.length, 'items');
      
      // Validate data structure
      const validatedData = this.validateData(transformedData);
      console.log(`✅ SurebetService (Markets) [${requestId}]: Validated data:`, validatedData.length, 'items');

      // Update cache
      this.updateCache(validatedData);
      console.log(`💾 SurebetService (Markets) [${requestId}]: Updated cache`);

      return { data: validatedData, error: null };
    } catch (error) {
      console.error(`❌ SurebetService (Markets) [${requestId}] processing error:`, error);
      console.error(`❌ SurebetService (Markets) [${requestId}] error details:`, {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
        hasResponse: !!error.response
      });
      
      // Check for 401 with invalid token and dispatch event here as fallback
      if (error.response?.status === 401) {
        const errorData = error.response?.data || {};
        const detail = errorData.detail || errorData.message || '';
        const description = errorData.description || '';
        const combinedText = `${detail} ${description}`.toLowerCase();
        
        if (combinedText.includes('invalid or inactive token') ||
            combinedText.includes('inactive') ||
            combinedText.includes('revoked')) {
          console.log('🚨 SurebetService: Invalid token detected, dispatching sessionConflict event (fallback)');
          window.dispatchEvent(new CustomEvent('auth:sessionConflict', {
            detail: {
              reason: 'session_invalid',
              message: 'Your session is not valid or has been revoked. Please log in again.',
              endpoint: error.config?.url,
              status: 401,
              timestamp: new Date().toISOString(),
              errorData: errorData
            }
          }));
        }
      }
      
      return this.handleError(error);
    }
  }

  /**
   * Build query parameters for API request
   */
  static buildQueryParams(params) {
    const queryParams = {};

    // Add bookmakers parameter
    if (params.bookmakers && params.bookmakers.length > 0) {
      // Convert bookmaker IDs to API format and join with comma
      const apiFormattedBookmakers = convertBookmakerIdsToApiFormat(params.bookmakers);
      queryParams.bookmakers = apiFormattedBookmakers.join(',');
    }

    // Add sport filter
    if (params.sport) {
      queryParams.sport = params.sport;
    }

    // Add championship filter
    if (params.championship) {
      queryParams.championship = params.championship;
    }

    // Add profitable only filter
    if (params.profitableOnly !== undefined) {
      queryParams.profitableOnly = params.profitableOnly;
    }

    return queryParams;
  }

  /**
   * Transform a single MarketsAnalysisDTO item
   */
  static transformSurebetItem(item) {
    return {
      id: item.id,
      event: {
        id: item.event?.id || 0,
        date: item.event?.date || '',
        time: item.event?.time || '',
        teamOne: item.event?.teamOne || '',
        teamTwo: item.event?.teamTwo || '',
        championship: item.event?.championship || '',
        sport: item.event?.sport || '',
      },
      marginFlag: item.marginFlag || false,
      margins: item.margins || {},
    };
  }

  /**
   * Transform response data (array of MarketsAnalysisDTO)
   */
  static transformResponse(data) {
    if (!Array.isArray(data)) {
      return [];
    }

    return data.map(item => this.transformSurebetItem(item));
  }

  /**
   * Transform public preview wrapper from GET /api/core/markets-analysis/masked
   *
   * @param {Object|Array} responseData - PublicMarketsAnalysisPreviewDto or legacy array
   * @returns {{ maskedItems: Array, demoItem: Object|null, totalActiveOpportunities: number, totalActiveEvents: number }}
   */
  static transformMaskedPreviewResponse(responseData) {
    if (Array.isArray(responseData)) {
      const maskedItems = this.transformResponse(responseData);
      return {
        maskedItems,
        demoItem: null,
        totalActiveOpportunities: this.countProfitableMarginRows(maskedItems),
        totalActiveEvents: maskedItems.length,
      };
    }

    let demoItem = null;

    if (responseData.demoItem) {
      const transformedDemo = this.transformResponse([responseData.demoItem]);
      demoItem = transformedDemo.length > 0 ? transformedDemo[0] : null;
    }

    const maskedItems = this.excludeDemoFromMaskedItems(
      this.transformResponse(responseData.maskedItems || []),
      demoItem
    );

    const totalActiveOpportunities = Number(responseData.totalActiveOpportunities);
    const totalActiveEvents = Number(responseData.totalActiveEvents);

    return {
      maskedItems,
      demoItem,
      totalActiveOpportunities: Number.isFinite(totalActiveOpportunities) && totalActiveOpportunities >= 0
        ? totalActiveOpportunities
        : this.countProfitableMarginRows(maskedItems),
      totalActiveEvents: Number.isFinite(totalActiveEvents) && totalActiveEvents >= 0
        ? totalActiveEvents
        : maskedItems.length,
    };
  }

  /**
   * Remove live-demo row from masked list (defensive; backend should already exclude it).
   */
  static excludeDemoFromMaskedItems(maskedItems, demoItem) {
    if (!demoItem || !Array.isArray(maskedItems)) {
      return maskedItems || [];
    }

    const demoAnalysisId =
      demoItem.id !== undefined && demoItem.id !== null ? Number(demoItem.id) : null;
    const demoEventId =
      demoItem.event?.id !== undefined && demoItem.event?.id !== null
        ? Number(demoItem.event.id)
        : null;

    return maskedItems.filter((item) => {
      if (
        demoAnalysisId !== null
        && item.id !== undefined
        && item.id !== null
        && Number(item.id) === demoAnalysisId
      ) {
        return false;
      }
      if (
        demoEventId !== null
        && item.event?.id !== undefined
        && item.event?.id !== null
        && Number(item.event.id) === demoEventId
      ) {
        return false;
      }
      return true;
    });
  }

  /**
   * Count profitable margin rows across all events (fallback when API totals absent)
   */
  static countProfitableMarginRows(items) {
    if (!Array.isArray(items)) {
      return 0;
    }

    let count = 0;
    items.forEach(surebet => {
      const margins = surebet.margins || {};
      Object.values(margins).forEach(margin => {
        if (margin && margin.value > 0) {
          count++;
        }
      });
    });
    return count;
  }

  /**
   * Validate data structure
   */
  static validateData(data) {
    if (!Array.isArray(data)) {
      return [];
    }

    return data.filter(item => {
      try {
        // Validate required fields
        if (!item.id || !item.event) {
          return false;
        }

        // Validate event fields
        const requiredEventFields = VALIDATION_RULES.REQUIRED_EVENT_FIELDS;
        const hasAllEventFields = requiredEventFields.every(field => 
          item.event.hasOwnProperty(field)
        );

        if (!hasAllEventFields) {
          return false;
        }

        // Validate margins if present
        if (item.margins && typeof item.margins === 'object') {
          for (const [marginKey, marginData] of Object.entries(item.margins)) {
            // Validate margin structure
            if (!marginData.hasOwnProperty('value') || !Array.isArray(marginData.coefficients)) {
              return false;
            }

            // Validate margin value
            if (marginData.value < VALIDATION_RULES.MIN_MARGIN || marginData.value > VALIDATION_RULES.MAX_MARGIN) {
              return false;
            }

            // Validate coefficients
            if (!this.validateCoefficients(marginData.coefficients)) {
              return false;
            }
          }
        }

        return true;
      } catch (error) {
        console.error('Validation error for item:', item, error);
        return false;
      }
    });
  }

  /**
   * Validate coefficients array
   */
  static validateCoefficients(coefficients) {
    if (!Array.isArray(coefficients) || coefficients.length === 0) {
      return false;
    }

    return coefficients.every(coef => {
      // Check required fields
      const requiredFields = VALIDATION_RULES.REQUIRED_COEFFICIENT_FIELDS;
      const hasAllFields = requiredFields.every(field => coef.hasOwnProperty(field));

      if (!hasAllFields) {
        return false;
      }

      // Validate coefficient value
      if (coef.value < VALIDATION_RULES.MIN_COEFFICIENT || coef.value > VALIDATION_RULES.MAX_COEFFICIENT) {
        return false;
      }

      return true;
    });
  }

  /**
   * Retry API call with exponential backoff
   */
  static async retryApiCall(apiCallFn, retryCount = 0) {
    try {
      return await apiCallFn();
    } catch (error) {
      // Check if error is retryable
      const isRetryable = this.isRetryableError(error);
      
      // Check if we should retry
      if (isRetryable && retryCount < this.retryConfig.maxRetries) {
        // Calculate delay with exponential backoff
        const delay = this.retryConfig.retryDelay * Math.pow(2, retryCount);
        
        // Wait before retrying
        await this.delay(delay);
        
        // Retry
        return await this.retryApiCall(apiCallFn, retryCount + 1);
      }

      // Max retries reached or non-retryable error
      throw error;
    }
  }

  /**
   * Check if error is retryable
   */
  static isRetryableError(error) {
    // Network errors are retryable
    if (!error.response) {
      return true;
    }

    // Check status code
    const statusCode = error.response?.status;
    return this.retryConfig.retryableStatusCodes.includes(statusCode);
  }

  /**
   * Handle API errors
   */
  static handleError(error) {
    console.error('SurebetService error:', error);

    // Handle specific HTTP status codes
    if (error.response) {
      const { status, data } = error.response;

      switch (status) {
        case 400:
          return {
            data: null,
            error: data?.detail || data?.message || 'Invalid request parameters',
          };

        case 401:
          return {
            data: null,
            error: 'Authentication required. Please log in.',
          };

        case 402:
          // Payment required - subscription issue
          // This will be handled by the global interceptor
          return {
            data: null,
            error: 'Active subscription required',
          };

        case 403:
          return {
            data: null,
            error: 'Access forbidden. You do not have permission.',
          };

        case 404:
          return {
            data: null,
            error: 'Service not found. Please try again later.',
          };

        case 429:
          return {
            data: null,
            error: 'Too many requests. Please slow down.',
          };

        case 500:
        case 502:
        case 503:
        case 504:
          return {
            data: null,
            error: 'Server error. Please try again later.',
          };

        default:
          return {
            data: null,
            error: data?.detail || data?.message || 'An unexpected error occurred',
          };
      }
    }

    // Network error (no response)
    if (error.request) {
      return {
        data: null,
        error: 'Network error. Please check your internet connection.',
      };
    }

    // Other errors
    return {
      data: null,
      error: error.message || 'An unexpected error occurred',
    };
  }

  /**
   * Check if cache is valid
   */
  static isCacheValid() {
    if (!this.cache.data || !this.cache.timestamp) {
      return false;
    }

    const now = Date.now();
    const age = now - this.cache.timestamp;

    return age < this.cache.ttl;
  }

  /**
   * Check if masked cache is valid
   */
  static isMaskedCacheValid() {
    if (!this.maskedCache.data || !this.maskedCache.timestamp) {
      return false;
    }

    const now = Date.now();
    const age = now - this.maskedCache.timestamp;

    return age < this.maskedCache.ttl;
  }

  /**
   * Update cache
   */
  static updateCache(data) {
    this.cache.data = data;
    this.cache.timestamp = Date.now();
  }

  /**
   * Update masked cache
   */
  static updateMaskedCache(data) {
    this.maskedCache.data = data;
    this.maskedCache.timestamp = Date.now();
  }

  /**
   * Clear cache
   */
  static clearCache() {
    this.cache.data = null;
    this.cache.timestamp = null;
  }

  /**
   * Clear masked cache
   */
  static clearMaskedCache() {
    this.maskedCache.data = null;
    this.maskedCache.timestamp = null;
  }

  /**
   * Get cache status
   */
  static getCacheStatus() {
    return {
      hasData: !!this.cache.data,
      timestamp: this.cache.timestamp,
      age: this.cache.timestamp ? Date.now() - this.cache.timestamp : null,
      isValid: this.isCacheValid(),
    };
  }

  /**
   * Delay helper
   */
  static delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Get bookmaker list
   * Mock endpoint - returns available bookmakers
   */
  static async getBookmakers() {
    try {
      // This would be a real API call in production
      // For now, return from constants
      const { BOOKMAKERS } = await import('../constants/surebetTypes');
      
      const bookmakerList = Object.entries(BOOKMAKERS).map(([key, name]) => ({
        id: key,
        name: name,
      }));

      return { data: bookmakerList, error: null };
    } catch (error) {
      return this.handleError(error);
    }
  }

  /**
   * Get sports list
   * Mock endpoint - returns available sports
   */
  static async getSports() {
    try {
      // This would be a real API call in production
      const sports = [
        { id: 'football', name: { bg: 'Футбол', en: 'Football' } },
        { id: 'basketball', name: { bg: 'Баскетбол', en: 'Basketball' } },
        { id: 'tennis', name: { bg: 'Тенис', en: 'Tennis' } },
        { id: 'volleyball', name: { bg: 'Волейбол', en: 'Volleyball' } },
        { id: 'handball', name: { bg: 'Хандбал', en: 'Handball' } },
      ];

      return { data: sports, error: null };
    } catch (error) {
      return this.handleError(error);
    }
  }
}

export default SurebetService;

